/**
 * PURPOSE: Navegar rápidamente entre deportes autorizados del mismo evento.
 * RESPONSIBILITIES: Mostrar flecha, buscador, selección actual y cierre accesible con Escape/click externo.
 * COLLABORATORS: CategoriaSection.jsx y menuSportsService.js.
 * ROUTE: winscore/src/components/SportSwitcher/SportSwitcher.jsx
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiCheck, FiChevronDown, FiSearch } from "react-icons/fi";

const normalize = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const SportSwitcher = ({ sports, currentSportId, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
    setQuery("");
  }, [currentSportId]);

  const filteredSports = useMemo(() => {
    const normalizedQuery = normalize(query);
    if (!normalizedQuery) return sports;
    return sports.filter((sport) =>
      normalize([sport.displayName, sport.name, sport.acronym].filter(Boolean).join(" ")).includes(
        normalizedQuery
      )
    );
  }, [query, sports]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-label="Cambiar deporte"
        className={`flex h-10 w-10 items-center justify-center rounded-full border transition ${
          open
            ? "border-blue-500 bg-blue-50 text-blue-700"
            : "border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:text-blue-600"
        }`}
      >
        <FiChevronDown className={`text-2xl transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open ? (
        <div className="absolute left-1/2 top-[calc(100%+10px)] z-[9999] w-[min(92vw,420px)] -translate-x-1/2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/20">
          <div className="border-b border-slate-100 p-3">
            <p className="mb-2 px-1 text-[10px] font-black uppercase tracking-[0.2em] text-blue-600">
              Deportes del evento
            </p>
            <label className="relative block">
              <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar deporte..."
                className="w-full rounded-xl border border-slate-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </label>
          </div>
          <div className="max-h-80 overflow-y-auto p-2">
            {filteredSports.map((sport) => {
              const selected = Number(sport.idsport) === Number(currentSportId);
              return (
                <button
                  key={sport.idsport}
                  type="button"
                  onClick={() => onSelect(sport)}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                    selected ? "bg-blue-50 text-blue-800" : "hover:bg-slate-50"
                  }`}
                >
                  {sport.displayImage || sport.image_path ? (
                    <img src={sport.displayImage || sport.image_path} alt="" className="h-10 w-10 rounded-xl bg-slate-100 object-cover" />
                  ) : (
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-xs font-black text-slate-500">
                      {(sport.acronym || sport.name || "--").slice(0, 2)}
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-black text-slate-800">
                      {sport.displayName || sport.name}
                    </span>
                    {sport.acronym ? (
                      <span className="block text-xs font-semibold text-slate-400">{sport.acronym}</span>
                    ) : null}
                  </span>
                  {selected ? <FiCheck className="shrink-0 text-blue-600" /> : null}
                </button>
              );
            })}
            {filteredSports.length === 0 ? (
              <div className="p-6 text-center text-sm font-semibold text-slate-500">
                No hay coincidencias.
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SportSwitcher;
